import type { PropType } from "vue";

import { computed, ref, toRefs } from "vue";

import type { DefineProps } from "@/lib/composables/componentComposable";

import { color, size } from "@/lib/components/logic/atoms/input/props";
import {
  emitsDefinition,
  propsDefinition,
} from "@/lib/composables/componentComposable";

type AlertLive = "assertive" | "off" | "polite";

const scoped = propsDefinition({
  size,
});

const props = propsDefinition({
  closable: {
    type: Boolean,
    default: false,
  },
  color,
  // Opt-in: an always-on live region interrupts screen readers on static alerts
  live: {
    type: String as PropType<AlertLive>,
    default: "off",
    validator: (value: string) =>
      ["off", "polite", "assertive"].includes(value),
  },
  prefixIcon: {
    type: String,
    required: false,
  },
  suffixIcon: {
    type: String,
    required: false,
  },
  ...scoped,
});

const emits = emitsDefinition(["close"]);

type UseAlertProps = DefineProps<typeof props>;
type UseAlertEmit = (event: "close") => void;

function use(props: UseAlertProps, emit?: UseAlertEmit) {
  const propRefs = toRefs(props);
  // Dismissal is view-only; the alert reappears on remount
  const isVisible = ref(true);

  // assertive interrupts whatever is being read; polite waits for a pause
  const liveAttrs = computed(() => {
    if (propRefs.live.value === "off") {
      return {};
    }

    return {
      role: propRefs.live.value === "assertive" ? "alert" : "status",
      "aria-live": propRefs.live.value,
      "aria-atomic": true,
    };
  });

  function close() {
    isVisible.value = false;
    emit?.("close");
  }

  return {
    size: propRefs.size,
    color: propRefs.color,
    prefixIcon: propRefs.prefixIcon,
    suffixIcon: propRefs.suffixIcon,
    closable: propRefs.closable,
    liveAttrs,
    isVisible,
    close,
  };
}

export type { AlertLive, UseAlertEmit, UseAlertProps };
export { emits, props, scoped, use };
